import type { SignupCarrierType } from 'data/DTOs';
import { captureError, logWarning } from 'shared/utils/datadog';
import { getFirstTouchFields } from 'carrier/attribution/firstTouchStorage';

type CarrierHubSpotValues = SignupCarrierType & {
  company_name?: string;
  role_other?: string;
  comment?: string;
};

export type CarrierHubSpotStage =
  | 'step1_verify'
  | 'step2_additional'
  | 'step3_signup';

const HUBSPOT_CARRIER_FORM_PORTAL_ID = '44142879';

export function getHubSpotCarrierFormGuid() {
  const formGuid = import.meta.env.VITE_HUBSPOT_CARRIER_FORM_GUID;

  if (!formGuid) {
    throw new Error('VITE_HUBSPOT_CARRIER_FORM_GUID is not configured');
  }

  return formGuid;
}

export function getHubSpotCarrierFormSubmitUrl() {
  return `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_CARRIER_FORM_PORTAL_ID}/${getHubSpotCarrierFormGuid()}`;
}

export function getHubSpotUtk(): string | undefined {
  const match = document.cookie.match(/hubspotutk=([^;]+)/);
  return match?.[1]?.trim() || undefined;
}

export function getHubSpotSubmissionContext() {
  const firstTouch = getFirstTouchFields();
  const hutk = getHubSpotUtk();

  return {
    pageUri: firstTouch.signup_landing_page_url ?? window.location.href,
    pageName: document.title,
    ...(hutk ? { hutk } : {}),
  };
}

/** Convert mixed values to HubSpot-safe string fields and skip empty values. */
export function toHubSpotFields(
  rawFields: Record<string, unknown>,
): Record<string, string> {
  const fields: Record<string, string> = {};

  for (const [name, value] of Object.entries(rawFields)) {
    if (!value) {
      continue;
    }
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      fields[name] = String(value);
    }
  }

  return fields;
}

function isRetryableHubSpotError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }

  if (error instanceof Error) {
    return (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network')
    );
  }

  return false;
}

async function submitToHubSpotWithRetry(
  fields: Record<string, string>,
  stage: CarrierHubSpotStage,
): Promise<unknown> {
  try {
    return await submitToHubSpot(fields);
  } catch (error) {
    if (stage !== 'step1_verify' || !isRetryableHubSpotError(error)) {
      throw error;
    }

    logWarning('HubSpot submission retrying after network error', { stage });
    return await submitToHubSpot(fields);
  }
}

export async function submitToHubSpot(
  fields: Record<string, string>,
): Promise<unknown> {
  const payload = {
    fields: Object.entries(fields).map(([name, value]) => ({ name, value })),
    context: getHubSpotSubmissionContext(),
  };

  const response = await fetch(getHubSpotCarrierFormSubmitUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const message = `HubSpot submission failed: ${response.status}`;
    logWarning(message, { status: response.status });
    throw new Error(message);
  }

  return response.json();
}

function getRefferalSource(
  values: Partial<CarrierHubSpotValues>,
): string | undefined {
  if (values.comment === 'Other' && values.referralSource) {
    return String(values.referralSource);
  }
  if (values.comment) {
    return String(values.comment);
  }
  return undefined;
}

function buildHubSpotFields(
  carrierValues: Partial<CarrierHubSpotValues>,
): Record<string, string> {
  const roleValue =
    carrierValues.role === 'Other'
      ? carrierValues.role_other
      : carrierValues.role;

  return toHubSpotFields({
    ...getFirstTouchFields(),
    email: carrierValues.email,
    firstname: carrierValues.first_name,
    lastname: carrierValues.last_name,
    company: carrierValues.company_name,
    phone: carrierValues.phoneNumber,
    number_of_drivers: carrierValues.number_of_trucks,
    usdot: carrierValues.country === 'US' ? carrierValues.usdot : undefined,
    country: carrierValues.country,
    role: roleValue,
    heard_about_us: getRefferalSource(carrierValues),
  });
}

export async function submitCarrierHubSpot(
  values: Record<string, unknown>,
  options: { stage: CarrierHubSpotStage },
) {
  const carrierValues = values as Partial<CarrierHubSpotValues>;
  const { stage } = options;

  try {
    await submitToHubSpotWithRetry(buildHubSpotFields(carrierValues), stage);
  } catch (e: unknown) {
    captureError(
      e instanceof Error ? e : new Error(String(e)),
      `CarrierHubSpot#submit:${stage}`,
      { stage, hasEmail: Boolean(carrierValues.email) },
    );
  }
}
